import { useState, useEffect } from "react";

interface CodePreviewProps {
  url: string;
  extension: string;
}

// Max bytes to fetch — avoid loading huge files into memory
const MAX_BYTES = 512 * 1024; // 512 KB

export function CodePreview({ url }: CodePreviewProps) {
  const [text, setText] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    setText(null);
    setError(false);
    setTruncated(false);

    fetch(url)
      .then(async (res) => {
        const blob = await res.blob();
        const slice = blob.size > MAX_BYTES ? blob.slice(0, MAX_BYTES) : blob;
        if (blob.size > MAX_BYTES) setTruncated(true);
        return slice.text();
      })
      .then(setText)
      .catch(() => setError(true));
  }, [url]);

  if (error) return <p className="text-sm text-red-400">Failed to load file</p>;

  if (text === null) {
    return (
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-600 border-t-brand-400" />
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border border-gray-700 bg-gray-950">
      {truncated && (
        <div className="flex-shrink-0 border-b border-gray-700 bg-yellow-900/20 px-4 py-1.5 text-xs text-yellow-400">
          Showing first 512 KB — download the file to see all content
        </div>
      )}
      <pre className="flex-1 overflow-auto p-4 text-xs leading-relaxed text-gray-200">
        <code>{text}</code>
      </pre>
    </div>
  );
}
